import { useMemo, useState } from 'react';
import { DropdownMenuCard } from 'components/Card';
import { OrganizationTreePopover } from 'components/OrganizationTreePopover';
import SortableFieldModal from 'components/SortableFieldsModal/SortableFieldsModal';
import { useGlobalFieldOrder } from 'hooks/useGlobalFieldOrder';
import { parsedCustomerSignatureSelector } from 'slices/auth.slice';
import { fieldMappingSelector, fieldsSelector } from 'slices/field.slice';
import { useAppSelector } from 'slices/store';
import { TEDrawerSection } from '@timeedit/ui-components';
import { Dropdown, Typography } from '@timeedit/ui-components/antd';

interface AttributesProps {
  value: any;
  onChangeValue: (key: string, value: any) => void;
}
const Attributes = ({ value, onChangeValue }: AttributesProps) => {
  const allFields = useAppSelector(fieldsSelector);
  const customerSignature = useAppSelector(parsedCustomerSignatureSelector);
  const fieldMap = useAppSelector(fieldMappingSelector);
  const {
    hasLocalChanges,
    changeGlobalOrder,
    saveGlobalOrder,
    reset,
    stagedGlobalFieldOrder,
  } = useGlobalFieldOrder();
  const [changeOrderModalVisible, setChangeOrderModalVisible] = useState(false);

  const fieldsOptions = useMemo(() => {
    if (allFields && allFields.length > 0) {
      return allFields
        ?.map((field) => ({
          key: field.id,
          label: `${field.extId} (${field.name})`,
          disable: value?.parentFields?.includes(field?.id),
          url: `/${customerSignature}/configuration/fields?item1=${field.id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    return [];
  }, [allFields, value?.parentFields, customerSignature]);

  const globalOrderSortedField = useMemo<number[]>(() => {
    return [...(value?.fields ?? []), ...(value?.parentFields ?? [])].sort(
      (a, b) => {
        const firstFieldIndex = stagedGlobalFieldOrder?.findIndex(
          (field) => a === field.id
        );
        const secondFieldIndex = stagedGlobalFieldOrder?.findIndex(
          (field) => b === field.id
        );
        return firstFieldIndex - secondFieldIndex;
      }
    );
  }, [value, stagedGlobalFieldOrder]);

  const onChangeFields = (fieldsIds: string[]) => {
    // As we are only able to modify the fields that are on the object type, we need to filter out the parent fields
    const fieldsOnType = fieldsIds
      .map(Number)
      .filter((id) => !value?.parentFields?.includes(id));
    onChangeValue('fields', fieldsOnType);
  };

  return (
    <TEDrawerSection label="Attributes">
      <div className="wrapper-section">
        <div className="section-item">
          <div className="te-drawer-input">
            <DropdownMenuCard
              title="Fields"
              extra={
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'change-order',
                        label: 'Change order',
                        onClick: () => setChangeOrderModalVisible(true),
                      },
                    ],
                  }}
                >
                  <Typography.Link underline>More</Typography.Link>
                </Dropdown>
              }
              selectedKeys={globalOrderSortedField.map(String)}
              options={fieldsOptions as any}
              onChange={onChangeFields}
              data-testid="add-field"
            />
          </div>
        </div>
        <div className="section-item">
          <div className="te-drawer-input">
            <OrganizationTreePopover
              title="Organizations"
              tooltip="Only use org node that is under the parent node “oType”, for example “Type - Admin"
              value={value.organizations || []}
              onChange={(values) => onChangeValue('organizations', values)}
              data-testid="add-org"
            />
          </div>
        </div>
      </div>
      <SortableFieldModal
        visible={changeOrderModalVisible}
        setVisible={setChangeOrderModalVisible}
        fields={globalOrderSortedField.map((field) => fieldMap[field])}
        hasChanges={hasLocalChanges}
        onChangeOrder={changeGlobalOrder}
        onSave={saveGlobalOrder}
        onDiscard={reset}
      />
    </TEDrawerSection>
  );
};

export default Attributes;
